import { api } from './client';

// ───── Settings
export interface CompanySettings {
  id: number;
  name: string;
  address: string | null;
  gstin: string | null;
  stateCode: string | null;
  mobile: string | null;
  email: string | null;
  website: string | null;
  linkedin: string | null;
  facebook: string | null;
  instagram: string | null;
  logoData: string | null;
  logoDarkData: string | null;
  bankName: string | null;
  bankAccount: string | null;
  bankBranch: string | null;
  bankIfsc: string | null;
  termsConditions: string | null;
  darkMode: boolean;
  glassmorphism: boolean;
  aiAlerts: boolean;
  autoPrLowStock: boolean;
  enabledModules: string | null;
  docTemplates: string | null;
  /**
   * Which MRN → PO routes are open:
   *   'express' = catalogue → MRN → PO only
   *   'full'    = MRN → HOD → PR → RFQ → Quote → PO only
   *   'both'    = both routes offered on each MRN (operator picks)
   */
  procurementMode: 'express' | 'full' | 'both';
  /** Dispatch weight-check tolerance (± %) — Outward-GRN gross vs expected net. */
  weightTolerancePct?: number | string;
}

export interface SlaStep {
  id: string;
  category: string;
  name: string;
  responsibleRole: string;
  hours: number;
  warnAtPct: number;
  escalateTo: string | null;
  active: boolean;
  displayOrder: number;
}

export async function getSettings(): Promise<CompanySettings> {
  const r = await api.get<CompanySettings>('/settings');
  return r.data;
}
export async function updateSettings(
  payload: Partial<Omit<CompanySettings, 'id'>>,
): Promise<CompanySettings> {
  const r = await api.patch<CompanySettings>('/settings', payload);
  return r.data;
}
export async function listSlaSteps(): Promise<SlaStep[]> {
  const r = await api.get<SlaStep[]>('/settings/sla-steps');
  return r.data;
}

// ───── Notification / Email / WhatsApp Config
export interface NotificationConfig {
  id: number;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  smtpFrom: string | null;
  smtpFromName: string | null;
  smtpSsl: boolean;
  defaultCc: string | null;
  defaultBcc: string | null;
  autoEmailRfq: boolean;
  autoEmailInvoice: boolean;
  autoEmailPo: boolean;
  // Automated internal workflow emails — master switch + per-stage JSON map.
  autoWorkflowEmails: boolean;
  workflowStagesJson: string | null;
  autoDailySummary: boolean;
  smtpPassSet: boolean;
  // Invoice SMTP — dedicated account for invoices; other docs use the primary account.
  invoiceSmtpHost: string | null;
  invoiceSmtpPort: number | null;
  invoiceSmtpUser: string | null;
  invoiceSmtpFrom: string | null;
  invoiceSmtpFromName: string | null;
  invoiceSmtpSsl: boolean;
  invoiceSmtpPassSet: boolean;
  // BusinessOnBot BSP — current, active WhatsApp path.
  bonbEndpoint: string | null;
  bonbDefaultTemplate: string | null;
  bonbLanguage: string | null;
  bonbApiKeySet: boolean;
  // Phase 2 — document sends (template with document header).
  bonbDocumentTemplate: string | null;
  bonbPublicBaseUrl: string | null;
  // Approved BSP templates by purpose (registered once, reused).
  bonbTplQuotation: string | null;
  bonbTplInvoice: string | null;
  bonbTplSalesOrder: string | null;
  bonbTplPo: string | null;
  bonbTplPrApproval: string | null;
  bonbTplEnquiryAck: string | null;
  bonbTplDailySummary: string | null;
  // Legacy Meta/QR columns still present in the DB (read-only, not edited here).
  waMethod: string | null;
  waProvider: string | null;
  waPhoneNumberId: string | null;
  waBusinessId: string | null;
  waPhoneNumber: string | null;
  waStatus: string | null;
  waWebhookUrl: string | null;
  waAccessTokenSet: boolean;
}

export async function getNotificationConfig(): Promise<NotificationConfig> {
  const r = await api.get<NotificationConfig>('/settings/notification-config');
  return r.data;
}

export async function updateNotificationConfig(
  payload: Partial<
    Omit<NotificationConfig, 'id' | 'smtpPassSet' | 'invoiceSmtpPassSet' | 'waAccessTokenSet' | 'bonbApiKeySet'> & {
      smtpPass?: string;
      invoiceSmtpPass?: string;
      bonbApiKey?: string;
    }
  >,
): Promise<NotificationConfig> {
  const r = await api.patch<NotificationConfig>('/settings/notification-config', payload);
  return r.data;
}

export async function testEmail(toEmail: string): Promise<{ ok: boolean; message: string }> {
  const r = await api.post<{ ok: boolean; message: string }>('/settings/test-email', { toEmail });
  return r.data;
}

// ───── WhatsApp (BusinessOnBot BSP)
export async function testWhatsApp(
  toPhone: string,
  templateName?: string,
): Promise<{ ok: boolean; message: string; template: string }> {
  const r = await api.post<{ ok: boolean; message: string; template: string }>(
    '/settings/test-whatsapp',
    { toPhone, templateName },
  );
  return r.data;
}

export async function sendDocEmail(
  docType: string,
  docId: number,
  toEmail: string,
  subject?: string,
  body?: string,
): Promise<{ ok: boolean; message: string }> {
  const r = await api.post<{ ok: boolean; message: string }>('/settings/send-doc-email', {
    docType, docId, toEmail, subject, body,
  });
  return r.data;
}

export async function sendDocWhatsApp(
  docType: string,
  docId: number,
  toPhone: string,
  message?: string,
): Promise<{ ok: boolean; message: string }> {
  const r = await api.post<{ ok: boolean; message: string }>('/settings/send-doc-whatsapp', {
    docType, docId, toPhone, message,
  });
  return r.data;
}

export async function sendPortalLinkWhatsApp(
  vendorId: number,
  portalUrl: string,
  rfqNo: string,
  message?: string,
): Promise<{ ok: boolean; message: string; method: string; phone: string }> {
  const r = await api.post<{ ok: boolean; message: string; method: string; phone: string }>(
    '/settings/send-portal-whatsapp',
    { vendorId, portalUrl, rfqNo, message },
  );
  return r.data;
}

// ───── Users
export interface AdminUser {
  id: number;
  fullName: string;
  username: string;
  email: string;
  phone: string | null;
  roleId: number;
  roleCode: string;
  role: string;
  department: string | null;
  status: 'Active' | 'Inactive';
  isSystemRole: boolean;
  lastLoginAt: string | null;
  joinedOn: string | null;
}

export async function listUsers(): Promise<AdminUser[]> {
  const r = await api.get<AdminUser[]>('/users');
  return r.data;
}
export async function createUser(payload: {
  username: string;
  email?: string;
  fullName: string;
  password: string;
  roleId: number;
  phone?: string;
  department?: string;
  joinedOn?: string;
}): Promise<{ id: number; username: string }> {
  const r = await api.post('/users', payload);
  return r.data;
}
export async function updateUser(
  id: number,
  payload: {
    fullName?: string;
    email?: string;
    phone?: string;
    department?: string;
    roleId?: number;
    notes?: string;
  },
): Promise<AdminUser> {
  const r = await api.patch<AdminUser>(`/users/${id}`, payload);
  return r.data;
}
export async function resetUserPassword(id: number, newPassword: string): Promise<void> {
  await api.post(`/users/${id}/reset-password`, { newPassword });
}
export async function toggleUserActive(id: number): Promise<AdminUser> {
  const r = await api.post<AdminUser>(`/users/${id}/toggle-active`);
  return r.data;
}
export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/users/${id}`);
}

// ───── Per-user permission overrides ─────
export interface UserPermissionModule {
  id: string;
  label: string;
  group: string;
  roleAllowed: boolean;
  /** null = inherit the role; true/false = explicit user override */
  override: boolean | null;
}
export interface UserPermissions {
  userId: number;
  roleId: number;
  roleCode: string;
  roleName: string;
  isSuperAdmin: boolean;
  modules: UserPermissionModule[];
}
export async function getUserPermissions(id: number): Promise<UserPermissions> {
  const r = await api.get<UserPermissions>(`/users/${id}/permissions`);
  return r.data;
}
export async function setUserPermissions(
  id: number,
  overrides: { moduleId: string; allowed: boolean }[],
): Promise<{ ok: boolean; count: number }> {
  const r = await api.put<{ ok: boolean; count: number }>(`/users/${id}/permissions`, { overrides });
  return r.data;
}

// ───── Roles
export interface AdminRole {
  id: number;
  code: string;
  name: string;
  description: string | null;
  level: number;
  color: string | null;
  isSystem: boolean;
  userCount: number;
  permCount: number;
}
export interface PermissionsMatrix {
  roles: Array<{ id: number; code: string; name: string; color: string | null; isSystem: boolean }>;
  modules: Array<{
    id: string;
    label: string;
    group: string;
    allowedByRole: Record<string, boolean>;
  }>;
}

export async function listRoles(): Promise<AdminRole[]> {
  const r = await api.get<AdminRole[]>('/roles');
  return r.data;
}
export async function getPermissionsMatrix(): Promise<PermissionsMatrix> {
  const r = await api.get<PermissionsMatrix>('/roles/permissions-matrix');
  return r.data;
}
export async function createRole(payload: {
  code: string;
  name: string;
  description?: string;
  level?: number;
  color?: string;
}): Promise<AdminRole> {
  const r = await api.post<AdminRole>('/roles', payload);
  return r.data;
}
export async function updateRole(
  id: number,
  payload: { name?: string; description?: string; level?: number; color?: string },
): Promise<AdminRole> {
  const r = await api.patch<AdminRole>(`/roles/${id}`, payload);
  return r.data;
}
export async function setRolePermissions(
  id: number,
  patches: Array<{ moduleId: string; allowed: boolean }>,
): Promise<void> {
  await api.post(`/roles/${id}/permissions`, { patches });
}
export async function deleteRole(id: number): Promise<void> {
  await api.delete(`/roles/${id}`);
}

// ─── Internal (workflow handoff) emails — Email Templates → Internal Emails ───
export interface StageEmailInfo {
  key: string;
  label: string;
  action: string;
  enabled: boolean;
  goesTo: string;
  recipients: string[];
  cc: string[];
  defaultSubject: string;
  defaultBody: string;
}

export async function getStageEmails(): Promise<StageEmailInfo[]> {
  const r = await api.get<StageEmailInfo[]>('/notifications/stage-emails');
  return r.data;
}

export async function previewStageEmail(stage: string, subject: string, body: string): Promise<{ subject: string; html: string }> {
  const r = await api.post<{ subject: string; html: string }>('/notifications/stage-emails/preview', { stage, subject, body });
  return r.data;
}
